import type { ICheckoutContext } from '@/types/context';
import type { PayFromExpressCheckout } from '@/types/payment';
import { readChargebeeErrorText } from './chargebeeErrorText';

/**
 * Chargebee rejects `handlePayment()` / `authorizeWith3ds()` when the shopper
 * dismisses the wallet sheet or 3DS challenge. A dismissal is not a decline —
 * callers reset state silently instead of surfacing decline copy.
 */
export const isChargebeeUserClosedError = (error: unknown): boolean =>
  /clos(e|ed)|cancel/i.test(readChargebeeErrorText(error));

/**
 * Apple validates the merchant against the origin that opens the sheet, so the
 * checkout host has to be a registered payment method domain on the gateway
 * behind the Chargebee site. Where it isn't, every tap fails identically for
 * every shopper, so callers collapse the box instead of re-arming a dead tap.
 *
 * The shapes this has to recognize, verbatim:
 * - Stripe, via Safari: "Could not create Apple Pay session. Make sure you
 *   aren't using an Apple Pay Sandbox account and have registered this domain
 *   on your Stripe account." — no "merchant validation" anywhere in it.
 * - WebKit: a `DOMException` whose `name` is `InvalidAccessError` and whose
 *   message says nothing about the domain.
 * - Chargebee: its `ValidateApplePaySession` call and `payment_method_domain`
 *   gateway codes.
 */
const APPLE_PAY_DOMAIN_FAILURE_PATTERNS = [
  /merchant.?validat/i,
  /payment_method_domain|apple_?pay_?domain/i,
  /validate_?apple_?pay_?session/i,
  /(could ?not|cannot|can'?t|unable to|fail(ed)? to) create[^.]{0,40}apple.?pay session/i,
  /register(ed)?[^.]{0,40}domain|domain[^.]{0,40}(register|verif|allow|support)/i,
  /apple.?pay sandbox account/i,
  /apple.?pay[^.]{0,40}not[^.]{0,20}(enabled|register|verif|allow)/i,
  /InvalidAccessError/i,
];

/**
 * WebKit reuses `InvalidAccessError` for two things a retry CAN fix — a session
 * started outside a user gesture, and a session already open — so those keep
 * the button armed.
 */
const APPLE_PAY_RETRYABLE_PATTERNS = [/user gesture/i, /already[^.]{0,30}(session|payment)/i];

export const isApplePayMerchantValidationError = (error: unknown): boolean => {
  const text = readChargebeeErrorText(error);
  if (APPLE_PAY_RETRYABLE_PATTERNS.some((pattern) => pattern.test(text))) return false;
  return APPLE_PAY_DOMAIN_FAILURE_PATTERNS.some((pattern) => pattern.test(text));
};

/**
 * Finalizes an intent the shopper has already authorized. Two callers:
 * Google Pay's `handlePayment()` rejecting with `payment_intent_authorized`
 * (an earlier attempt authorized but never finalized — the intent is
 * single-use, so re-opening the sheet can never succeed while the
 * authorization itself still stands), and the Apple Pay drop-in's `onSuccess`,
 * which fires with the intent already authorized inside Chargebee's iframe.
 * Mirrors the card handler's recovery.
 */
export const finalizeAlreadyAuthorizedWalletPayment = async (
  context: ICheckoutContext,
  paymentMethod: PayFromExpressCheckout
): Promise<void> => {
  const paymentIntent = context.subscription?.paymentIntent;
  if (!paymentIntent) return;
  context.actions.setIsExpressPaymentInProcess(true);
  await context.actions.runPayment({ paymentIntent }, paymentMethod);
};
